/**
 * Who gets a push for an ordinary thread message.
 *
 * Split out of `threads.ts` and importing nothing, so the rule can be tested
 * directly. It is here because the first version of this gate shipped with a
 * silent bug: it read `thread.kind === 'dm'` alone, which also silenced
 * must-reads sent to a group. Nothing failed, no test covered it, and the one
 * category that most needs to arrive would simply have stopped arriving.
 *
 * This does NOT cover @mentions. Those fan out separately and unconditionally
 * in `sendThreadMessage`, before this decision is consulted.
 */

/** Mirrors `ThreadKind` in threads.ts. Duplicated to keep this file importless. */
export type ThreadNotifyKind = 'dm' | 'group';

export type ThreadNotifyInput = {
  /** 'dm' is one-to-one; 'group' is 3 or more, up to the cap in threadLimits.ts. */
  threadKind: ThreadNotifyKind;
  /** Admin board posts and replies follow each member's board setting. */
  adminBoard?: boolean;
  /** The sender ticked the must-read (ackRequired) toggle. */
  mustRead: boolean;
  /** Anyone left to notify after @mentioned people are removed. */
  hasOtherRecipients: boolean;
  /**
   * The opening message of a parents-only class group (DECISIONS 57 and 59).
   * It is the class's teacher telling its parents something, the way a class
   * post does, so it rings like a class post rather than staying quiet like
   * a group reply. Replies in that group stay quiet as usual.
   */
  classAnnouncement?: boolean;
};

/**
 * Bobbie, 2026-08-08: "Direct messages should always notify as well. Replies
 * to a group we don't need."
 *
 * A one-to-one message is addressed to you personally and there is nobody else
 * to answer it, so missing one has a real cost. A busy group thread is the
 * opposite: every "yes!" and "thank you!" would buzz twenty phones, which is
 * how people switch notifications off for good and then miss the thing that
 * actually mattered.
 *
 * A must-read is not an ordinary reply, so it is exempt. That is the whole
 * point of the toggle: the sender is saying "this one is for you" to a group
 * without having to @mention each person by name.
 */
export function shouldNotifyThreadMessage(input: ThreadNotifyInput): boolean {
  if (!input.hasOtherRecipients) return false;
  if (input.mustRead) return true;
  if (input.classAnnouncement === true) return true;
  if (input.threadKind === 'group' && input.adminBoard === true) return true;
  return input.threadKind === 'dm';
}
